import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import StatsCards from "../components/StatsCards/StatsCards";
import "./AdminDashboard.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiMapPin, FiNavigation } from "react-icons/fi";

function AdminDashboard() {
  const navigate = useNavigate();

  const [branches, setBranches] = useState([
    {
      id: 1,
      name: "North Hospital Branch",
      location: "New York",
      access: true,
      doctors: 42,
      patients: 560,
    },
    {
      id: 2,
      name: "South Medical Center",
      location: "Los Angeles",
      access: true,
      doctors: 38,
      patients: 490,
    },
  ]);

  // Device states
  const [devices] = useState([
    {
      id: 1,
      name: "CT Scanner",
      location: "North Hospital Branch",
      status: "active",
      lastMaintenance: "2023-08-15",
      nextMaintenance: "2023-09-15",
    },
    {
      id: 2,
      name: "MRI Machine",
      location: "South Medical Center",
      status: "maintenance",
      lastMaintenance: "2023-08-01",
      nextMaintenance: "2023-08-30",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");

  const toastConfig = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    style: {
      background: "#1f2937",
      color: "#fff",
      borderRadius: "8px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    },
  };

  // Hospital functions
  const toggleBranchAccess = (id, e) => {
    e.stopPropagation();
    setBranches(
      branches.map((branch) => {
        if (branch.id === id) {
          const newAccess = !branch.access;
          if (!newAccess) {
            toast.error(
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ marginRight: "8px" }}>🔒</span>
                <div>
                  <p style={{ margin: 0, fontWeight: 500 }}>Access Denied</p>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "0.85em",
                      opacity: 0.9,
                    }}
                  >
                    Hospital access has been restricted
                  </p>
                </div>
              </div>,
              toastConfig
            );
          } else {
            toast.success(
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ marginRight: "8px" }}>🔓</span>
                <div>
                  <p style={{ margin: 0, fontWeight: 500 }}>Access Granted</p>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "0.85em",
                      opacity: 0.9,
                    }}
                  >
                    Hospital access has been restored
                  </p>
                </div>
              </div>,
              toastConfig
            );
          }
          return { ...branch, access: newAccess };
        }
        return branch;
      })
    );
  };

  const handleViewAllHospitals = () => {
    navigate("/dashboard/hospital");
  };

  const handleHospitalClick = (hospital) => {
    if (hospital.access) {
      navigate(`/dashboard/hospital/${hospital.id}`);
    }
  };

  const getHospitalIdByName = (hospitalName) => {
    const hospital = branches.find((branch) => branch.name === hospitalName);
    return hospital ? hospital.id : null;
  };

  const handleDeviceClick = (device) => {
    const hospitalId = getHospitalIdByName(device.location);
    if (hospitalId) {
      navigate(`/dashboard/hospital/${hospitalId}`);
    }
  };

  // Filter functions
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredBranches = branches.filter(
    (branch) =>
      branch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      branch.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDevices = devices.filter(
    (device) =>
      device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="admin-layout">
      <Navbar />
      <main className="admin-main">
        <div className="admin-header">
          <div className="admin-welcome-section">
            <div className="breadcrumb">
              <Link to="/admin-dashboard">Admin Dashboard</Link>
            </div>
            <h1>Welcome Admin</h1>
            <p>Here's what's happening with your medical portal today.</p>
          </div>
          <div className="admin-actions">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search..."
                className="search-input"
                value={searchQuery}
                onChange={handleSearch}
              />
              <button className="search-btn">🔍</button>
            </div>
          </div>
        </div>

        <StatsCards />

        <div className="admin-dashboard-content">
          <div className="admin-main-content">
            <div className="admin-cards-container">
              {/* Hospital Section */}
              <div className="admin-content-card admin-branches">
                <div className="card-header">
                  <h2>Hospitals</h2>
                  <button
                    className="add-branch-btn"
                    onClick={handleViewAllHospitals}
                  >
                    <FiNavigation /> View All
                  </button>
                </div>

                <div className="branch-cards">
                  {filteredBranches.map((branch) => (
                    <div
                      key={branch.id}
                      className="branch-card"
                      onClick={() => handleHospitalClick(branch)}
                      style={{
                        cursor: branch.access ? "pointer" : "not-allowed",
                      }}
                      data-disabled={!branch.access}
                    >
                      <div className="branch-header">
                        <h3>{branch.name}</h3>
                        <div
                          className="toggle-container"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={branch.access}
                              onChange={(e) => toggleBranchAccess(branch.id, e)}
                            />
                            <span className="slider round"></span>
                          </label>
                          <span className="access-label">
                            {branch.access ? "Access Granted" : "Access Denied"}
                          </span>
                        </div>
                      </div>
                      <div className="branch-location">
                        <FiMapPin /> {branch.location}
                      </div>
                      <div className="branch-stats">
                        <div className="branch-stat">
                          <span className="stat-label">Doctors</span>
                          <span className="stat-value">{branch.doctors}</span>
                        </div>
                        <div className="branch-stat">
                          <span className="stat-label">Patients</span>
                          <span className="stat-value">{branch.patients}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Device Section */}
              <div className="admin-content-card admin-devices">
                <div className="card-header">
                  <h2>Devices</h2>
                  <button
                    className="add-branch-btn"
                    onClick={() => handleDeviceClick(devices[0])}
                  >
                    <FiNavigation /> View All
                  </button>
                </div>

                <div className="branch-cards">
                  {filteredDevices.map((device) => (
                    <div key={device.id} className="branch-card">
                      <div className="branch-header">
                        <h3>{device.name}</h3>
                        <span className={`device-status ${device.status}`}>
                          {device.status.charAt(0).toUpperCase() +
                            device.status.slice(1)}
                        </span>
                      </div>
                      <div className="branch-location">
                        <FiMapPin /> {device.location}
                      </div>
                      <div className="branch-stats">
                        <div className="branch-stat">
                          <span className="stat-label">Last Maintenance</span>
                          <span className="stat-value">
                            {device.lastMaintenance}
                          </span>
                        </div>
                        <div className="branch-stat">
                          <span className="stat-label">Next Maintenance</span>
                          <span className="stat-value">
                            {device.nextMaintenance}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
}

export default AdminDashboard;
